import animationMinutesStyles from 'css/src/components/animated-explainer-videos.module.scss';

import React, { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';

import { useEffectiveAnimationMinutes } from 'js/src/components/animated-explainer-videos/hooks/useEffectiveAnimationMinutes';
import {
    formatAnimationMinutesCost,
    getBillingLockReason,
} from 'js/src/components/animated-explainer-videos/utils/billingUtils';
import { PurchaseAnimationMinutesModal } from 'js/src/components/billing-settings/PurchaseAnimationMinutesModal';
import { Toast } from 'js/src/components/common/Toast';
import {
    BUTTON_SIZES,
    BUTTON_TOOLTIP_PLACEMENTS,
    PrimaryTextButton,
} from 'js/src/components/common/ui/Button';
import MemoizedUpsellIcon, { ICON_SIZES } from 'js/src/components/common/ui/UpsellIcon';
import { LOCKED_FEATURES, MODAL_IDS, showModal } from 'js/src/data/modals';
import { loadUsageLimits } from 'js/src/data/usage-limit/usage-limit-operations';
import { getGenXBillingContext } from 'js/src/data/usage-limit/usage-limit-selectors';
import { trackUpsellClicked } from 'js/src/libs/analytics-utils';
import { useAppDispatch } from 'js/src/libs/RootState';

interface AnimationMinutesBadgeProps {
    /** The balance cannot cover what was asked, so it reads as empty whatever is left. */
    isOut?: boolean;
    /** Runs before the add-on checkout redirect, so unsaved video edits survive it. */
    onBeforeRedirectToCheckout?: () => Promise<void>;
}

/**
 * The animation-minutes balance with the same add-minutes / upgrade affordance as the AI Media
 * tab, for chat cards that just spent some.
 */
const AnimationMinutesBadge: React.FC<AnimationMinutesBadgeProps> = ({
    isOut = false,
    onBeforeRedirectToCheckout,
}) => {
    const dispatch = useAppDispatch();
    const { isLoaded, effectiveLimitSeconds, effectiveUsedSeconds } =
        useEffectiveAnimationMinutes();
    const billingContext = useSelector(getGenXBillingContext);
    const [showPurchaseModal, setShowPurchaseModal] = useState(false);

    const isPaidPlanUser = SERVER.USER.plan_price > 0;
    const billingLockReason = getBillingLockReason();

    const handleAddMinutes = useCallback(() => {
        if (billingLockReason !== null) {
            return;
        }
        trackUpsellClicked('agent_ai_media_add_minutes');
        setShowPurchaseModal(true);
    }, [billingLockReason]);

    const handleUpgrade = useCallback(() => {
        trackUpsellClicked('agent_ai_media_upgrade');
        dispatch(
            showModal({
                modal_id: MODAL_IDS.FEATURE_LOCK,
                modal_context: {
                    featureName: LOCKED_FEATURES.ANIMATION_MINUTES,
                    subCategory: 'animation minutes',
                },
            }),
        );
    }, [dispatch]);

    const handleClosePurchaseModal = useCallback(() => setShowPurchaseModal(false), []);

    const handlePurchaseSuccess = useCallback(() => {
        setShowPurchaseModal(false);
        Toast.success('Animation minutes successfully added.');
        dispatch(loadUsageLimits());
    }, [dispatch]);

    if (!isLoaded) {
        return null;
    }

    const remainingSeconds = Math.max(0, effectiveLimitSeconds - effectiveUsedSeconds);
    const isEmpty = isOut || effectiveLimitSeconds <= 0 || remainingSeconds <= 0;

    return (
        <div
            className={`${animationMinutesStyles['gen-x-animation-minutes-status']} mt-l5-8`}
            data-testid="ai-media-minutes-badge"
        >
            <div className={animationMinutesStyles['gen-x-animation-minutes-row']}>
                <i
                    translate="no"
                    className={classNames(
                        'material-symbols-outlined',
                        animationMinutesStyles['gen-x-animation-minutes-icon'],
                        {
                            [animationMinutesStyles['gen-x-animation-minutes-icon--empty']]:
                                isEmpty,
                        },
                    )}
                >
                    search_activity
                </i>
                <span
                    className={classNames('font-l5 font-l5--small--medium', {
                        [animationMinutesStyles['gen-x-animation-minutes-count--empty']]: isEmpty,
                    })}
                >
                    {remainingSeconds > 0 ? formatAnimationMinutesCost(remainingSeconds) : '0 mins'}
                </span>
                <span className="font-l5 font-l5--small color-grey-600">left</span>
            </div>
            {isPaidPlanUser ? (
                <PrimaryTextButton
                    text="Add more minutes"
                    onClick={handleAddMinutes}
                    size={BUTTON_SIZES.SM}
                    disabled={billingLockReason !== null}
                    tooltipTitle={billingLockReason ?? undefined}
                    tooltipPlacement={BUTTON_TOOLTIP_PLACEMENTS.BOTTOM}
                />
            ) : (
                <a
                    onClick={handleUpgrade}
                    className="d-flex align-items-center font-l5 font-l5--small--medium color-grey-900 cursor-pointer"
                >
                    <MemoizedUpsellIcon size={ICON_SIZES.SM} />
                    <span className="color-warning-500 me-l5-4">Upgrade</span>
                    for more minutes
                </a>
            )}
            {isPaidPlanUser && (
                <PurchaseAnimationMinutesModal
                    showing={showPurchaseModal}
                    onHidden={handleClosePurchaseModal}
                    onSuccess={handlePurchaseSuccess}
                    billingContext={billingContext}
                    context="clip"
                    onBeforeRedirectToCheckout={onBeforeRedirectToCheckout}
                />
            )}
        </div>
    );
};

export default AnimationMinutesBadge;
